/**
 * Model Methods
 * API methods for saving, listing, and downloading models
 */

import { getSession } from '../../../../../core/auth/supabase-auth';
import { getApiBaseUrl } from '../core';
import { TrainingApiResponse, ApiError, ModelListResponse } from '../types';

/**
 * Save trained model
 *
 * @param sessionId - Training session ID
 * @param modelName - Optional model name to save (default: 'best_model')
 * @returns Promise with save model response
 */
export async function saveModel(sessionId: string, modelName?: string): Promise<TrainingApiResponse> {
  const requestBody = { model_name: modelName || 'best_model' };

  try {
    // Get authentication token
    const session = await getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token available. Please log in.');
    }

    const response = await fetch(`${getApiBaseUrl()}/save-model/${sessionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (!response.ok) {
      console.error(`Model save failed:`, data);
      throw new Error(data.message || `HTTP ${response.status}: Model save failed`);
    }

    return data;

  } catch (error) {
    console.error(`Model save error:`, error);

    const apiError: ApiError = new Error(error instanceof Error ? error.message : 'Model save failed');
    apiError.code = 'MODEL_SAVE_FAILED';
    apiError.details = error;
    apiError.recoverySuggestions = [
      'Ensure the training session has completed successfully',
      'Check that trained models are available',
      'Try again in a few moments'
    ];
    throw apiError;
  }
}

/**
 * List all available models for a session
 */
export async function listModels(sessionId: string): Promise<TrainingApiResponse<ModelListResponse>> {
  try {
    // Get authentication token
    const session = await getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token available. Please log in.');
    }

    const response = await fetch(`${getApiBaseUrl()}/list-models/${sessionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      }
    });

    const data = await response.json();

    if (!response.ok) {
      console.error(`List models failed:`, data);
      throw new Error(data.message || `HTTP ${response.status}: List models failed`);
    }

    return data;
  } catch (error) {
    console.error('Error in listModels:', error);
    throw error;
  }
}

/**
 * Download a model file
 */
export async function downloadModel(sessionId: string, modelName?: string): Promise<void> {
  try {
    // Get authentication token
    const session = await getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token available. Please log in.');
    }

    const url = modelName
      ? `${getApiBaseUrl()}/download-model/${sessionId}?model_name=${encodeURIComponent(modelName)}`
      : `${getApiBaseUrl()}/download-model/${sessionId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error(`Download model failed:`, errorData);
      throw new Error(errorData.message || `HTTP ${response.status}: Download model failed`);
    }

    // Get filename from response headers or use default
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `model_${sessionId}.h5`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Create blob and download
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    console.log(`Model downloaded: ${filename}`);
  } catch (error) {
    console.error('Error in downloadModel:', error);
    throw error;
  }
}

/**
 * List models from Supabase database
 */
export async function listModelsDatabase(sessionId: string): Promise<TrainingApiResponse<ModelListResponse>> {
  try {
    // Get authentication token
    const session = await getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token available. Please log in.');
    }

    const response = await fetch(`${getApiBaseUrl()}/list-models-database/${sessionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      }
    });

    const data = await response.json();

    if (!response.ok) {
      console.error(`List database models failed:`, data);
      throw new Error(data.message || `HTTP ${response.status}: List database models failed`);
    }

    return data;
  } catch (error) {
    console.error('Error in listModelsDatabase:', error);
    throw error;
  }
}

/**
 * Download model in .h5 format from Supabase database
 */
export async function downloadModelH5(sessionId: string, modelFilename?: string): Promise<void> {
  try {
    // Get authentication token
    const session = await getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token available. Please log in.');
    }

    // Build URL with optional filename query parameter
    const url = new URL(`${getApiBaseUrl()}/download-model-h5/${sessionId}`);
    if (modelFilename) {
      url.searchParams.append('filename', modelFilename);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error(`Download H5 model failed:`, errorData);
      throw new Error(errorData.message || `HTTP ${response.status}: Download H5 model failed`);
    }

    // Get filename from response headers or use default
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `trained_model_${sessionId}.h5`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Create blob and download
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    console.log(`H5 Model downloaded: ${filename}`);
  } catch (error) {
    console.error('Error in downloadModelH5:', error);
    throw error;
  }
}

/**
 * Prediction result interface
 */
export interface PredictionResult {
  predictions: number[];
  model_used: string;
  timestamp: string;
}

/**
 * Make predictions using a trained model
 * Note: Backend endpoint POST /api/training/predict/{session_id} must be implemented
 */
export async function predictWithModel(
  sessionId: string,
  modelFilename: string,
  inputData: Record<string, number>[]
): Promise<TrainingApiResponse<PredictionResult>> {
  try {
    // Get authentication token
    const session = await getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token available. Please log in.');
    }

    const response = await fetch(`${getApiBaseUrl()}/predict/${sessionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        model_filename: modelFilename,
        input_data: inputData
      })
    });

    const data = await response.json();

    if (!response.ok) {
      console.error(`Prediction failed:`, data);
      throw new Error(data.message || `HTTP ${response.status}: Prediction failed`);
    }

    return data;
  } catch (error) {
    console.error('Error in predictWithModel:', error);
    throw error;
  }
}
